import { useEffect, useCallback, useRef } from "react";
import { useQueryClient, QueryClient } from "@tanstack/react-query";
import { getApiBaseUrl } from "@/lib/http";
import { queryKeys } from "@/lib/query-keys";

export interface NoteProcessingEvent {
  noteId: string;
  status: "processing" | "completed" | "failed";
  entitiesCount?: number;
  error?: string;
  runId?: string;
}

export interface UseNoteProcessingOptions {
  onProcessingComplete?: (event: NoteProcessingEvent) => void;
  onProcessingFailed?: (event: NoteProcessingEvent) => void;
  autoInvalidate?: boolean;
}

export function subscribeToNoteProcessing(
  noteId: string,
  queryClient: QueryClient,
  options: UseNoteProcessingOptions = {},
): () => void {
  const {
    onProcessingComplete,
    onProcessingFailed,
    autoInvalidate = true,
  } = options;

  const baseUrl = getApiBaseUrl();
  const url = `${baseUrl}/api/notes/${noteId}/processing`;

  const eventSource = new EventSource(url, { withCredentials: true });

  eventSource.onmessage = (event) => {
    try {
      const data: NoteProcessingEvent = JSON.parse(event.data);

      if (data.status === "completed") {
        if (autoInvalidate) {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.notes.detail(noteId),
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.entities.all,
          });
          void queryClient.invalidateQueries({
            queryKey: queryKeys.notes.all,
          });
        }
        onProcessingComplete?.(data);
        eventSource.close();
      } else if (data.status === "failed") {
        onProcessingFailed?.(data);
        eventSource.close();
      }
    } catch {
      console.error("[subscribeToNoteProcessing] Failed to parse event data");
    }
  };

  eventSource.onerror = () => {
    console.error("[subscribeToNoteProcessing] EventSource error");
    eventSource.close();
  };

  return () => {
    eventSource.close();
  };
}

export interface UseNoteProcessingOptions {
  onProcessingComplete?: (event: NoteProcessingEvent) => void;
  onProcessingFailed?: (event: NoteProcessingEvent) => void;
  autoInvalidate?: boolean;
}

export function useNoteProcessing(
  noteId: string | undefined,
  options: UseNoteProcessingOptions = {},
) {
  const {
    onProcessingComplete,
    onProcessingFailed,
    autoInvalidate = true,
  } = options;
  const queryClient = useQueryClient();
  const eventSourceRef = useRef<EventSource | null>(null);

  const subscribe = useCallback(() => {
    if (!noteId) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const baseUrl = getApiBaseUrl();
    const url = `${baseUrl}/api/notes/${noteId}/processing`;

    const eventSource = new EventSource(url, { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.onmessage = (event) => {
      try {
        const data: NoteProcessingEvent = JSON.parse(event.data);

        if (data.status === "completed") {
          if (autoInvalidate) {
            void queryClient.invalidateQueries({
              queryKey: queryKeys.notes.detail(noteId),
            });
            void queryClient.invalidateQueries({
              queryKey: queryKeys.entities.all,
            });
            void queryClient.invalidateQueries({
              queryKey: queryKeys.notes.all,
            });
          }
          onProcessingComplete?.(data);
          eventSource.close();
        } else if (data.status === "failed") {
          onProcessingFailed?.(data);
          eventSource.close();
        }
      } catch {
        console.error("[useNoteProcessing] Failed to parse event data");
      }
    };

    eventSource.onerror = () => {
      console.error("[useNoteProcessing] EventSource error");
      eventSource.close();
    };

    return () => {
      eventSource.close();
    };
  }, [
    noteId,
    queryClient,
    autoInvalidate,
    onProcessingComplete,
    onProcessingFailed,
  ]);

  const unsubscribe = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  return {
    subscribe,
    unsubscribe,
  };
}

export function useAutoNoteProcessing(
  noteId: string | undefined,
  shouldSubscribe: boolean = false,
  options: UseNoteProcessingOptions = {},
) {
  const { subscribe, unsubscribe } = useNoteProcessing(noteId, options);

  useEffect(() => {
    if (shouldSubscribe && noteId) {
      const cleanup = subscribe();
      return cleanup;
    }
  }, [noteId, shouldSubscribe, subscribe]);

  return { unsubscribe };
}
